import { Field, useFormikContext } from 'formik';
import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';
import { DropdownComponent } from '../../components/prime-formik';
import { getNumberOfEmployeesLabel } from '../../utils/AppUtil';
import { AccountType } from '../../shared/Constants';
import useSubscription from '../../hooks/useSubscription';
import PresentSubscription from './PresentSubscription';

const i18nMessages = defineMessages({
  changeNoLimitSubscriptionTo: {
    id: 'account.subscription.change-present-no-limit-subscription-to',
    defaultMessage: 'Change present NoLimit subscription to',
  },
  numberOfCandidatesIncluded: {
    id: 'account.subscription.number-of-candidates-included',
    defaultMessage: 'Number of candidates included:',
  },
  unlimited: {
    id: 'account.subscription.unlimited',
    defaultMessage: 'Unlimited',
  },
  numberOfEmployeesIncluded: {
    id: 'account.subscription.number-of-employees-included',
    defaultMessage: 'Number of employees included:',
  },
  prePaidSubscriptionPeriod: {
    id: 'account.subscription.pre-paid-subscription-period',
    defaultMessage: 'Pre-paid subscription period:',
  },
  attachSubscriptionTo: {
    id: 'account.subscription.attach-subscription-to',
    defaultMessage: 'Attach this subscription to:',
  },
});

const NewSubscriptionOptions = ({
  numbersOfEmployees,
  subscriptionPeriods,
  clientAccount,
  computeSubscriptionCost,
}) => {
  const {
    values: { numberOfEmployees, subscriptionPeriodId },
    setFieldValue,
  } = useFormikContext();
  const { formatMessage } = useIntl();

  const { getNumberOfEmployeesOptions, getSubscriptionPeriodOptions, buildAccountLabel } = useSubscription();

  const {
    type: clientAccountType,
    subscriptionInformation: {
      currentSubscription: { numberOfEmployees: currentSubscriptionNumberOfEmployees },
    },
  } = clientAccount;

  const clientAccountIsGscOrGp = clientAccountType === AccountType.GSC || clientAccountType === AccountType.GP;

  return (
    <section className="form-section">
      <p>
        <span className="underlined">{formatMessage(i18nMessages.changeNoLimitSubscriptionTo)}</span>
      </p>
      <div className="flex-box">
        <div>{formatMessage(i18nMessages.numberOfCandidatesIncluded)}</div>
        <div>{formatMessage(i18nMessages.unlimited)}</div>
      </div>
      <div className="flex-box">
        <div>{formatMessage(i18nMessages.numberOfEmployeesIncluded)}</div>
        {clientAccountIsGscOrGp ? (
          <div>{getNumberOfEmployeesLabel(currentSubscriptionNumberOfEmployees)}</div>
        ) : (
          <Field
            id="numberOfEmployees"
            name="numberOfEmployees"
            component={DropdownComponent}
            options={getNumberOfEmployeesOptions(numbersOfEmployees, clientAccountIsGscOrGp)}
            filter
            filterBy="label,value"
            onChange={(event) => {
              setFieldValue('numberOfEmployees', event.value);
              computeSubscriptionCost(event.value, subscriptionPeriodId);
            }}
          />
        )}
      </div>
      <div className="flex-box">
        <div>{formatMessage(i18nMessages.prePaidSubscriptionPeriod)}</div>
        <Field
          id="subscriptionPeriodId"
          name="subscriptionPeriodId"
          component={DropdownComponent}
          options={getSubscriptionPeriodOptions(subscriptionPeriods)}
          filter
          filterBy="label,value"
          onChange={(event) => {
            setFieldValue('subscriptionPeriodId', event.value);
            computeSubscriptionCost(numberOfEmployees, event.value);
          }}
        />
      </div>
      <div className="flex-box">
        <div>{formatMessage(i18nMessages.attachSubscriptionTo)}</div>
        <div>{buildAccountLabel(clientAccount)}</div>
      </div>
    </section>
  );
};

PresentSubscription.propTypes = {
  numbersOfEmployees: PropTypes.arrayOf(PropTypes.any).isRequired,
  subscriptionPeriods: PropTypes.arrayOf(PropTypes.any).isRequired,
  clientAccount: PropTypes.objectOf(PropTypes.any).isRequired,
  computeSubscriptionCost: PropTypes.func.isRequired,
};

export default NewSubscriptionOptions;
